import { match } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { ClaimType } from '~/utils/constants';
import { makeCrewAction, redirect } from '~/utils/routing';
import { crewOrderLineItemSchema } from '~/utils/schemas';

type AppState = Parameters<Parameters<typeof makeCrewAction>[0]>[1];

export const FormKeys = {
  claimId: 'claimId',
  lineItem: 'lineItem',
  claimType: 'claimType',
} as const;

const deleteSchema = zfd.formData({
  [FormKeys.claimId]: zfd.text(),
});

const handleDelete = (formData: FormData, appState: AppState) => {
  const { claimId } = deleteSchema.parse(formData);
  appState.dispatch(appState.actions.crewClaim.removeLineItemClaim(claimId));

  return {
    ok: true,
    message: 'Item removed',
  };
};

const postSchema = zfd.formData({
  [FormKeys.claimType]: zfd.text(z.nativeEnum(ClaimType)),
  [FormKeys.lineItem]: zfd.json(crewOrderLineItemSchema),
});

const handlePost = (formData: FormData, appState: AppState) => {
  const result = postSchema.safeParse(formData);

  if (!result.success) {
    return new Error('Malformed form data', {
      cause: {
        error: result.error,
        entries: Array.from(formData.entries()),
      },
    });
  }

  const { lineItem } = result.data;

  if (!lineItem) {
    return {
      ok: false,
      message: 'Selected item not found',
    };
  }

  const id = Math.random().toString(36).slice(2);

  appState.dispatch(
    appState.actions.lineItemClaim.startNewClaim({
      id,
      lineItem,
      quantity: 1,
      unitPrice: lineItem.unitPrice,
      unitTax: lineItem.unitTax,
      grams: lineItem.grams,
      customFields: {},
      comments: null,
      originalStoreOrderLineItemId: lineItem.id,
    }),
  );

  return redirect(`${id}/quantity`);
};

export const { action, useActionResult: useOrderClaimActionResult } =
  makeCrewAction(({ formData, request }, appState) =>
    match(request.method)
      .with('DELETE', () => handleDelete(formData, appState))
      .with('POST', () => handlePost(formData, appState))
      .otherwise(
        () =>
          new Error('Invalid request method', {
            cause: { method: request.method },
          }),
      ),
  );
